import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar as CalendarIcon, Trash2, Plus, Loader2, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useAttendance } from "../../hooks/useAttendance";
import { Holiday } from "@/types/attendance";
import { useState } from "react";

const HolidayList = () => {
  const { company } = useAuth();
  const slug = company?.slug || "";
  const { holidays, isLoadingHolidays, createHoliday, deleteHoliday } = useAttendance(slug);

  const [isAdding, setIsAdding] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ description: "", date: "" });

  const handleSave = async () => {
    if (!newHoliday.description || !newHoliday.date) return;
    
    try {
      await createHoliday.mutateAsync({ ...newHoliday, slug });
      setIsAdding(false);
      setNewHoliday({ description: "", date: "" });
    } catch (error) {
      // Error handled in hook
    }
  };

  if (isLoadingHolidays) {
    return (
      <Card className="border-slate-200/60 shadow-sm bg-white">
        <CardContent className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200/60 shadow-sm bg-white">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-medium flex items-center gap-2 text-slate-900">
          <CalendarIcon className="h-5 w-5 text-purple-600" />
          Días Festivos
        </CardTitle>
        <Button 
          variant="ghost" 
          size="icon" 
          className="hover:bg-purple-50 hover:text-purple-600 rounded-full"
          onClick={() => setIsAdding(!isAdding)}
        >
          {isAdding ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {isAdding && (
            <div className="p-3 bg-purple-50/50 rounded-xl border border-purple-100 space-y-3 animate-in slide-in-from-top-2 duration-300">
              <Input 
                placeholder="Nombre del festivo" 
                className="h-8 text-xs" 
                value={newHoliday.description}
                onChange={(e) => setNewHoliday({...newHoliday, description: e.target.value})}
              />
              <Input 
                type="date" 
                className="h-8 text-xs" 
                value={newHoliday.date}
                onChange={(e) => setNewHoliday({...newHoliday, date: e.target.value})}
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" className="h-7 text-[10px]" onClick={() => setIsAdding(false)}>
                  Cancelar
                </Button>
                <Button 
                  size="sm" 
                  className="h-7 text-[10px] bg-purple-600 hover:bg-purple-700"
                  onClick={handleSave}
                  disabled={!newHoliday.description || !newHoliday.date || createHoliday.isPending}
                >
                  {createHoliday.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Guardar"}
                </Button>
              </div>
            </div>
          )}

          {holidays?.map((holiday: Holiday) => (
            <div key={holiday.id} className="flex items-center justify-between group p-2 hover:bg-slate-50 rounded-lg transition-colors">
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-900">{holiday.description}</p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">{holiday.date}</span>
                  <Badge variant="outline" className="text-[10px] py-0 border-rose-200 text-rose-600 bg-rose-50 font-normal">
                    No laborable
                  </Badge>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-rose-600 hover:bg-rose-50 rounded-full"
                onClick={() => deleteHoliday.mutate(holiday.id)}
                disabled={deleteHoliday.isPending}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {(!holidays || holidays.length === 0) && !isAdding && (
            <div className="text-center py-8">
              <CalendarIcon className="h-8 w-8 text-slate-200 mx-auto mb-2" />
              <p className="text-xs text-slate-400 italic">No hay días festivos registrados.</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default HolidayList;
